// Rug Room Shots: the AI check of one image (SPEC_AICHECK 5.3). Shows the verdict chip, the hard fails, the
// one-sentence reason, the problems (as reject reasons), the nine criteria, the suggested regeneration note
// (Use as regenerate note / Copy), when / which model / prompt / cost, and Check again.
// Full in the generation detail dialog, compact in the compare view (only the criteria that are not OK, with
// "Show all 9"). The AI never approves, rejects or regenerates by itself: the two buttons only hand the note to
// the person's own Regenerate form, or ask for a new check (POST generations/{genId}/ai-check, a few cents).
// `ai` is the backend's aiCheck block (SPEC_AICHECK 4.1); null for an image that is not finished.
import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Stack, Typography, Chip, Button, Tooltip, CircularProgress, Alert,
} from '@mui/material';
import {
  MdCheckCircle, MdWarningAmber, MdCancel, MdHelpOutline, MdContentCopy, MdReplay, MdFactCheck, MdEditNote,
} from 'react-icons/md';
import rrsApi from './api';
import {
  AiChip, AI_CRITERIA_LABELS, AI_RESULT_LABELS, AI_HARD_FAIL_LABELS, AI_NOT_READY_TEXT,
  REJECT_REASON_LABELS, aiHasResult, aiCriteriaList, fmtNy, fmtNyFull, fmtUsd, conflictCode, serverText, errText,
  isDailyLimit, dailyLimitText, signalSpend, WARN_CODES,
} from './common';

const RESULT_ICON = {
  ok: { Icon: MdCheckCircle, color: 'success.main' },
  minor: { Icon: MdWarningAmber, color: 'warning.main' },
  major: { Icon: MdCancel, color: 'error.main' },
  not_sure: { Icon: MdHelpOutline, color: 'text.secondary' },
};

// Ask for one AI check of one image (also a re-check). Returns { ok, already, severity, message, res } so each
// caller shows it its own way (toast or an inline line). Never throws.
export const requestAiCheck = async (genId, itemId) => {
  try {
    const res = await rrsApi.aiCheckOne(genId);
    signalSpend();
    const paused = res && res.generationPaused
      ? ' Generation is paused: the check waits until it is resumed.' : '';
    return {
      ok: true,
      res,
      severity: 'success',
      message: `AI check queued${itemId ? ` for ${itemId}` : ''} (about ${fmtUsd(res && res.estUsd, 3)}). The result appears on the image when it is done.${paused}`,
    };
  } catch (e) {
    const code = conflictCode(e);
    if (isDailyLimit(e)) {
      signalSpend();
      return { ok: false, severity: 'error', message: dailyLimitText(e) };
    }
    // 409 checking ("This image is already being checked."): nothing new was queued, the open check continues.
    if (code === 'checking') return { ok: false, already: true, severity: 'info', message: serverText(e) };
    // worker_not_ready, not_succeeded, outputs_not_ready: the server's words, as a warning.
    if (code && WARN_CODES.includes(code)) return { ok: false, severity: 'warning', message: serverText(e) };
    return { ok: false, severity: 'error', message: code ? serverText(e) : errText(e) };
  }
};

const CriterionRow = ({ c }) => {
  const info = RESULT_ICON[c.result] || RESULT_ICON.not_sure;
  const { Icon } = info;
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.25 }}>
      <Tooltip title={AI_RESULT_LABELS[c.result] || c.result || ''}>
        <Box component="span" sx={{ color: info.color, display: 'inline-flex', mt: '2px', flex: '0 0 auto' }}>
          <Icon size={16} aria-label={AI_RESULT_LABELS[c.result] || c.result || ''} />
        </Box>
      </Tooltip>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
          {AI_CRITERIA_LABELS[c.id] || c.id}
        </Typography>
        {c.result && c.result !== 'ok' ? (
          <Typography variant="body2" component="span" sx={{ color: info.color, ml: 0.75 }}>
            {AI_RESULT_LABELS[c.result] || c.result}
          </Typography>
        ) : null}
        {c.note ? (
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>{c.note}</Typography>
        ) : null}
      </Box>
    </Stack>
  );
};

const AiCheckPanel = ({
  ai, item, compact = false, onCheckAgain, onUseNote, busy = false, enabled = true,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);
  useEffect(() => () => clearTimeout(copyTimer.current), []);
  // A new check (or another image) starts collapsed again.
  const checkId = ai ? ai.checkId : null;
  useEffect(() => { setShowAll(false); setCopied(false); }, [checkId]);

  if (!ai) return null;
  const has = aiHasResult(ai);
  const lastError = ai.lastError || null;
  const canAsk = enabled && !!onCheckAgain && !ai.checking;

  const checkButton = (label) => (canAsk ? (
    <Button
      size="small" variant="outlined" disabled={busy}
      startIcon={busy ? <CircularProgress size={14} color="inherit" /> : (has ? <MdReplay /> : <MdFactCheck />)}
      onClick={(e) => { e.stopPropagation(); onCheckAgain(); }}
    >
      {label}
    </Button>
  ) : null);

  // ---- no result yet: not checked / checking / could not check ----
  if (!has) {
    return (
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
          <AiChip ai={ai} />
          {ai.state === 'checking' && <Typography variant="body2" color="text.secondary">Checking with AI…</Typography>}
          {ai.state === 'unchecked' && <Typography variant="body2" color="text.secondary">Not checked yet.</Typography>}
        </Stack>
        {ai.state === 'error' && (
          <Typography variant="body2" color="warning.main">
            Could not check: {(lastError && lastError.errorText) || 'the AI check failed.'}
          </Typography>
        )}
        {enabled ? (
          ai.state !== 'checking' && checkButton(ai.state === 'error' ? 'Check again' : 'Check with AI')
        ) : (
          <Typography variant="caption" color="text.secondary">{AI_NOT_READY_TEXT}</Typography>
        )}
      </Stack>
    );
  }

  // ---- a result ----
  const criteria = aiCriteriaList(ai);
  const notOk = criteria.filter((c) => c.result !== 'ok');
  const shown = compact && !showAll ? notOk : criteria;
  const hardFails = Array.isArray(ai.hardFailCodes) ? ai.hardFailCodes : [];
  const problems = Array.isArray(ai.problems) ? ai.problems : [];
  const footer = [
    ai.checkedAt ? `Checked ${fmtNy(ai.checkedAt)}` : null,
    ai.model || null,
    ai.promptVersion || null,
    ai.costUsd !== null && ai.costUsd !== undefined ? fmtUsd(ai.costUsd, 3) : null,
    ai.origin ? (ai.origin === 'auto' ? 'automatic' : 'asked by a person') : null,
  ].filter(Boolean).join(' · ');

  const copyNote = async (e) => {
    e.stopPropagation();
    const text = ai.suggestedNote || '';
    let ok = false;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch (err) { /* fall back below */ }
    if (!ok) {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        ta.remove();
      } catch (err) { ok = false; }
    }
    if (ok) {
      setCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 1500);
    }
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
        <AiChip ai={ai} />
        {hardFails.map((h) => (
          <Chip key={h} size="small" color="error" variant="outlined" label={AI_HARD_FAIL_LABELS[h] || h.replace(/_/g, ' ')} />
        ))}
        {ai.checking && (
          <Typography variant="caption" color="text.secondary">Checking again…</Typography>
        )}
      </Stack>

      {ai.why ? <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{ai.why}</Typography> : null}

      {problems.length > 0 && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">Problems:</Typography>
          {problems.map((p) => (
            <Chip
              key={p} size="small" variant="outlined"
              color={ai.state === 'pass' ? 'default' : (ai.state === 'fail' ? 'error' : 'warning')}
              label={REJECT_REASON_LABELS[p] || p.replace(/_/g, ' ')}
              sx={{ height: 22 }}
            />
          ))}
        </Stack>
      )}

      {lastError && (
        <Alert severity="warning" sx={{ py: 0 }}>
          A newer check could not finish{lastError.at ? ` (${fmtNy(lastError.at)})` : ''}: {lastError.errorText || 'the AI check failed.'}
          {' '}The result shown is from the earlier check.
        </Alert>
      )}

      {criteria.length > 0 && (
        <Box>
          {compact && notOk.length === 0 && !showAll ? (
            <Typography variant="body2" color="success.main">All {criteria.length} checks OK.</Typography>
          ) : (
            <Stack spacing={0.25}>
              {shown.map((c) => <CriterionRow key={c.id} c={c} />)}
            </Stack>
          )}
          {compact && criteria.length > notOk.length && (
            <Button size="small" sx={{ mt: 0.25, px: 0.5, minWidth: 0 }} onClick={(e) => { e.stopPropagation(); setShowAll((s) => !s); }}>
              {showAll ? 'Show only the issues' : `Show all ${criteria.length}`}
            </Button>
          )}
        </Box>
      )}

      {ai.suggestedNote ? (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            Suggested note for a regeneration
          </Typography>
          <Box
            sx={{
              borderLeft: 3, borderColor: 'divider', bgcolor: 'action.hover', borderRadius: 1, px: 1.25, py: 0.75,
            }}
          >
            <Typography variant="body2" sx={{ fontStyle: 'italic', wordBreak: 'break-word' }}>{ai.suggestedNote}</Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
            {onUseNote && (
              <Tooltip title={`Opens the Regenerate form${item && item.itemId ? ` for ${item.itemId}` : ''} with this note added to the prompt. Nothing is sent until you confirm.`}>
                <Button
                  size="small" variant="outlined" startIcon={<MdEditNote />}
                  onClick={(e) => { e.stopPropagation(); onUseNote(ai.suggestedNote); }}
                >
                  Use as regenerate note
                </Button>
              </Tooltip>
            )}
            <Button size="small" startIcon={<MdContentCopy />} onClick={copyNote}>{copied ? 'Copied' : 'Copy'}</Button>
          </Stack>
        </Box>
      ) : null}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
        {footer ? (
          <Tooltip title={ai.checkedAt ? `${fmtNyFull(ai.checkedAt)} (New York)` : ''}>
            <Typography variant="caption" color="text.secondary">{footer}</Typography>
          </Tooltip>
        ) : null}
        <Box sx={{ flex: 1 }} />
        {enabled ? checkButton('Check again') : (
          <Typography variant="caption" color="text.secondary">{AI_NOT_READY_TEXT}</Typography>
        )}
      </Stack>
    </Stack>
  );
};

export default AiCheckPanel;
